import React, { forwardRef, useEffect, useState, useCallback } from "react";
import { Input, Form, Button, Collapse, message, Select } from "antd";
import { X } from "react-feather";
import FeatherIcon from "feather-icons-react";
import {
  createWarehouse,
  updateWarehouse,
  getWarehouse,
  createWarehouseAddress,
  updateWarehouseAddress,
  deleteWarehouseAddress,
  fetchBrandsBySelectedClients,
} from "../../core/api/settings";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
const { Panel } = Collapse;
const { Option } = Select;

const WarehouseDetailsView = forwardRef(
  (
    {
      isVisible,
      onClose,
      warehouseId,
      setWarehouseId,
      onResize,
      loadWarehouses,
      clientId,
    },
    ref
  ) => {
    const [form] = Form.useForm();
    const [addressForm] = Form.useForm();
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [warehouseName, setWarehouseName] = useState("");
    const [brands, setBrands] = useState([]);

    useEffect(() => {
      const sidebarWidth = isFullScreen ? window.innerWidth * 0.6 : 555;
      onResize(sidebarWidth);
    }, [isFullScreen, onResize]);

    const loadWarehouseDetails = useCallback(async () => {
      try {
        const response = await getWarehouse(warehouseId);
        const transformedAddresses = (response.data.addresses || []).map(
          (address) => ({
            ...address,
            id: address.id || null,
            name: address.name || "",
            address: address.address || "",
            contact_name: address.contact_name || "",
            email: address.email || "",
            phone_number: address.phone_number || "",
          })
        );
        form.setFieldsValue(response.data);
        setWarehouseName(response.data.name);
        addressForm.setFieldsValue({ addresses: transformedAddresses });
      } catch (err) {
        console.error("Failed to load warehouse details", err);
      }
    }, [warehouseId, form, addressForm, setWarehouseName]);

    useEffect(() => {
      if (warehouseId) {
        loadWarehouseDetails();
      } else {
        form.resetFields();
      }
    }, [warehouseId, form, loadWarehouseDetails]);

    useEffect(() => {
      const fetchBrands = async () => {
        try {
          if (clientId) {
            const response = await fetchBrandsBySelectedClients([clientId]);
            setBrands(response.data);
          }
        } catch (err) {
          console.error("Failed to fetch brands", err);
        }
      };

      fetchBrands();
    }, [clientId]);

    const handleSaveWarehouse = async () => {
      try {
        const values = await form.validateFields();
        let savedWarehouseId;
        if (warehouseId) {
          await updateWarehouse(warehouseId, values);
          savedWarehouseId = warehouseId;
        } else {
          const response = await createWarehouse(values);
          savedWarehouseId = response.data.id;
        }
        setWarehouseId(savedWarehouseId);
        loadWarehouses();
        message.success("Warehouse information saved successfully.");
      } catch (err) {
        console.error("Failed to save warehouse", err);
      }
    };

    const saveAddress = async (index) => {
      try {
        const addresses = addressForm.getFieldValue("addresses") || [];
        const address = addresses[index];
        if (!address) {
          message.error("Address data not found.");
          return;
        }

        if (address.id) {
          await updateWarehouseAddress(address.id, address);
          message.success(`${address.name} updated successfully.`);
        } else {
          const response = await createWarehouseAddress({
            ...address,
            warehouse: warehouseId,
          });
          addressForm.setFieldsValue({
            addresses: addresses.map((addr, idx) =>
              idx === index ? { ...addr, id: response.data.id } : addr
            ),
          });
          message.success(`${address.name} created successfully.`);
          loadWarehouses();
        }
      } catch (err) {
        console.error("Failed to save address", err);
        message.error(`Failed to save address ${index + 1}.`);
      }
    };

    const handleRemoveAddress = async (index, remove) => {
      const addresses = addressForm.getFieldValue("addresses") || [];
      const address = addresses[index];

      if (!address) {
        message.error("Address data not found.");
        return;
      }

      const result = await Swal.fire({
        title: "Are you sure?",
        text: `Do you really want to delete ${address.name}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "No, keep it",
      });

      if (result.isConfirmed) {
        try {
          if (address.id) {
            await deleteWarehouseAddress(address.id);
            message.success(`${address.name} deleted successfully.`);
          }
          remove(index);
        } catch (err) {
          console.error("Failed to delete address", err);
          message.error(`Failed to delete ${address.name}.`);
        }
      }
    };

    const data = useSelector((state) => state.toggle_header);

    return (
      <div
        ref={ref}
        className={`right-sidebar ${isVisible ? "visible" : ""}`}
        style={{
          position: "fixed",
          top: data ? 0 : 66,
          right: 0,
          height: "100%",
          zIndex: 1050,
          width: isFullScreen ? "60%" : "555px",
          backgroundColor: "#fff",
          boxShadow: "0 0 3px rgba(0, 0, 0, 0.6)",
          overflowY: "auto",
          transition: "transform 0.3s ease-in-out",
          transform: isVisible ? "translateX(0)" : "translateX(100%)",
        }}
      >
        <div
          className="sidebar-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "15px",
            borderBottom: "1px solid #ddd",
          }}
        >
          <h5>
            {warehouseId ? "Edit " + warehouseName : "Create New Warehouse"}
          </h5>
          <div>
            <button
              onClick={() => setIsFullScreen(!isFullScreen)}
              className="btn"
            >
              <FeatherIcon icon={isFullScreen ? "minimize" : "maximize"} />
            </button>
            <button
              className="btn"
              onClick={() => {
                form.resetFields();
                addressForm.resetFields();
                onClose();
              }}
            >
              <X />
            </button>
          </div>
        </div>

        <div className="modal-body" style={{ padding: "10px" }}>
          <Collapse defaultActiveKey={["1"]}>
            <Panel header="Warehouse Information" key="1">
              <Form form={form} layout="vertical">
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "5px",
                  }}
                >
                  <Form.Item name="brand_id" label="Brand" rules={[
                    { required: true, message: "Please select a brand" },
                  ]}>
                    <Select
                      placeholder="Select Brand"
                      value={form.getFieldValue("brand_id")}
                      onChange={(value) =>
                        form.setFieldsValue({ brand_id: value })
                      }
                      style={{ width: '100%' }}
                    >
                      {brands.map((brand) => (
                        <Option key={brand.id} value={brand.id}>
                          {brand.brand_name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item
                    label="Warehouse Name"
                    name="name"
                    rules={[
                      {
                        required: true,
                        message: "Please enter the warehouse name",
                      },
                    ]}
                  >
                    <Input placeholder="Enter Warehouse Name" />
                  </Form.Item>

                  <Form.Item label="Contact Name" name="contact_name">
                    <Input placeholder="Enter Contact Name" />
                  </Form.Item>
                  <Form.Item label="Email Address" name="email">
                    <Input type="email" placeholder="Enter Email Address" />
                  </Form.Item>
                  <Form.Item label="Phone Number" name="phone_number">
                    <Input type="tel" placeholder="Enter Phone Number" />
                  </Form.Item>
                  <Form.Item label="Address" name="address">
                    <Input.TextArea
                      rows={3}
                      placeholder="Enter Warehouse Address"
                    />
                  </Form.Item>
                </div>
                <Button
                  type="primary"
                  className="btn btn-primary"
                  onClick={handleSaveWarehouse}
                >
                  Save Warehouse
                </Button>
              </Form>
            </Panel>
          </Collapse>
          <br />
          <Form form={addressForm} layout="vertical">
            <Form.List name="addresses">
              {(fields, { add, remove }) => (
                <div>
                  {fields.map(({ key, name, fieldKey, ...restField }) => (
                    <Collapse key={key} style={{ marginBottom: 16 }}>
                      <Panel
                        header={
                          addressForm.getFieldValue([
                            "addresses",
                            name,
                            "name",
                          ]) || `Address ${name + 1}`
                        }
                        key={key}
                      >
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "5px",
                          }}
                        >
                          <Form.Item
                            {...restField}
                            name={[name, "name"]}
                            fieldKey={[fieldKey, "name"]}
                            label="Address Name"
                            rules={[
                              {
                                required: true,
                                message: "Please enter the address name",
                              },
                            ]}
                          >
                            <Input placeholder="Enter Address Name" />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            name={[name, "address"]}
                            fieldKey={[fieldKey, "address"]}
                            label="Address"
                            rules={[
                              {
                                required: true,
                                message: "Please enter the address",
                              },
                            ]}
                          >
                            <Input.TextArea
                              rows={3}
                              style={{ overflow: "auto" }}
                              placeholder="Enter Address"
                            />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            name={[name, "contact_name"]}
                            fieldKey={[fieldKey, "contact_name"]}
                            label="Contact Name"
                          >
                            <Input placeholder="Enter Contact Name" />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            name={[name, "email"]}
                            fieldKey={[fieldKey, "email"]}
                            label="Email Address"
                          >
                            <Input placeholder="Enter Email Address" />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            name={[name, "phone_number"]}
                            fieldKey={[fieldKey, "phone_number"]}
                            label="Phone Number"
                          >
                            <Input placeholder="Enter Phone Number" />
                          </Form.Item>
                        </div>
                        <div className="d-flex justify-content-between">
                          <Button
                            type="primary"
                            className="btn btn-primary"
                            onClick={() => saveAddress(name)}
                          >
                            Save Address
                          </Button>
                          <Button
                            type="dashed"
                            danger
                            onClick={() => handleRemoveAddress(name, remove)}
                          >
                            Remove Address
                          </Button>
                        </div>
                      </Panel>
                    </Collapse>
                  ))}
                  <Button
                    icon={<FeatherIcon icon="plus" size={18} />}
                    type="dashed"
                    onClick={() => {
                      if (warehouseId) {
                        const warehouseName =
                          form.getFieldValue("name") || "Warehouse";
                        const currentAddresses =
                          addressForm.getFieldValue("addresses") || [];
                        add({
                          name: `${warehouseName} (${
                            currentAddresses.length + 1
                          })`,
                        });
                      } else {
                        message.warning(
                          "Please save the warehouse before adding addresses"
                        );
                      }
                    }}
                    block
                  >
                    Add Address
                  </Button>
                </div>
              )}
            </Form.List>
          </Form>
        </div>
        <div style={{ height: 100 }}></div>
      </div>
    );
  }
);

WarehouseDetailsView.displayName = "WarehouseDetailsView";

export default WarehouseDetailsView;
